/**
 * Comparison chart preferences.
 *
 * `CompareChartPrefs` holds the view controls that follow the user across
 * every comparison; every field is optional because the chart owns the
 * defaults (they depend on the loaded series) and a missing field means
 * "use the default". `CompareChartByKey` holds the per-comparison state,
 * keyed by the comparison's URL key (/portal/compare/<pbKey>).
 */

import { DATE_PRESET_KEYS, type DatePreset } from '../shared/date-preset';

const CHART_VIEWS = ['daily-smoothed', 'daily', 'weekly', 'weekly-7d', 'weekly-fixed'] as const;
const PRESETS = DATE_PRESET_KEYS;
const GROUP_BY = ['sku', 'brand'] as const;
const METRIC_TYPES = ['cumulative', 'marketshare'] as const;
const VALUE_MODES = ['enheter', 'omsattning'] as const;

// Bounds of the sidebar's Övrigt input.
const OTHER_MAX_PCT_MIN = 0;
const OTHER_MAX_PCT_MAX = 90;

export interface CompareChartPrefs {
  chartView?: (typeof CHART_VIEWS)[number];
  groupBy?: (typeof GROUP_BY)[number];
  metricType?: (typeof METRIC_TYPES)[number];
  valueMode?: (typeof VALUE_MODES)[number];
  showCampaignPattern?: boolean;
  showCategorySum?: boolean;
  /** Preset id only; absolute dates are never stored. */
  preset?: DatePreset;
}

export interface CompareChartKeyPrefs {
  /** Selected channel keys, as the series store enumerates them. */
  vendors?: string[];
  /** Brand (or SKU) labels unchecked in the chart sidebar. */
  hiddenBrands?: string[];
  otherMaxPct?: number;
}

export type CompareChartByKey = Readonly<Record<string, CompareChartKeyPrefs>>;

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return !!raw && typeof raw === 'object' && !Array.isArray(raw);
}

function oneOf<T extends string>(raw: unknown, allowed: readonly T[]): T | undefined {
  return typeof raw === 'string' && (allowed as readonly string[]).includes(raw)
    ? (raw as T)
    : undefined;
}

function stringList(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  return raw.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

export function mergeCompareChart(raw: unknown): CompareChartPrefs {
  if (!isRecord(raw)) return {};
  const out: CompareChartPrefs = {};
  const chartView = oneOf(raw['chartView'], CHART_VIEWS);
  if (chartView) out.chartView = chartView;
  const groupBy = oneOf(raw['groupBy'], GROUP_BY);
  if (groupBy) out.groupBy = groupBy;
  const metricType = oneOf(raw['metricType'], METRIC_TYPES);
  if (metricType) out.metricType = metricType;
  const valueMode = oneOf(raw['valueMode'], VALUE_MODES);
  if (valueMode) out.valueMode = valueMode;
  if (typeof raw['showCampaignPattern'] === 'boolean') {
    out.showCampaignPattern = raw['showCampaignPattern'];
  }
  if (typeof raw['showCategorySum'] === 'boolean') out.showCategorySum = raw['showCategorySum'];
  const preset = oneOf(raw['preset'], PRESETS);
  if (preset) out.preset = preset;
  return out;
}

function mergeCompareChartKey(raw: unknown): CompareChartKeyPrefs {
  if (!isRecord(raw)) return {};
  const out: CompareChartKeyPrefs = {};
  const vendors = stringList(raw['vendors']);
  if (vendors) out.vendors = vendors;
  const hiddenBrands = stringList(raw['hiddenBrands']);
  if (hiddenBrands) out.hiddenBrands = hiddenBrands;
  const pct = raw['otherMaxPct'];
  if (typeof pct === 'number' && Number.isFinite(pct)) {
    out.otherMaxPct = Math.min(OTHER_MAX_PCT_MAX, Math.max(OTHER_MAX_PCT_MIN, Math.round(pct)));
  }
  return out;
}

/** Entries that carry nothing after cleaning are dropped. */
export function mergeCompareChartByKey(raw: unknown): CompareChartByKey {
  if (!isRecord(raw)) return {};
  const out: Record<string, CompareChartKeyPrefs> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (!key) continue;
    const merged = mergeCompareChartKey(value);
    if (Object.keys(merged).length > 0) out[key] = merged;
  }
  return out;
}
